<template>
    <StepShell :embedded="embedded">
        <h1 v-if="!embedded" class="greeting">
            {{ firstName ? t("onboarding.calendarFirst.introStep.greeting", { name: firstName }) : t("onboarding.calendarFirst.introStep.greetingAnonymous") }}
        </h1>

        <div class="sections">
            <NadiaCard :title="title">
                <p class="card-body">{{ body }}</p>
                <StepPoints :points="points" />
            </NadiaCard>

            <section class="privacy">
                <div>
                    <h2 class="privacy-title">{{ t("onboarding.calendarFirst.introStep.privacy.title") }}</h2>
                    <ul class="privacy-list">
                        <li v-for="promise in privacyPromises" :key="promise">{{ promise }}</li>
                    </ul>
                </div>
                <BaseButton theme="tertiary" size="sm" class="privacy-link" @click="privacyOpen = true">
                    {{ t("onboarding.calendarFirst.introStep.privacy.readMore") }}
                    <i aria-hidden="true" class="bi bi-arrow-up-right"></i>
                </BaseButton>
            </section>
        </div>

        <StepActions flush>
            <BaseButton theme="primary" @click="$emit('complete')">{{ t("onboarding.calendarFirst.introStep.next") }}</BaseButton>
        </StepActions>

        <PrivacyNoticeDialog v-model:open="privacyOpen" />
    </StepShell>
</template>

<script setup lang="ts">
/**
 * Show privacy commitments alongside Nadia's introduction so users see them without advancing a carousel.
 */
import BaseButton from "~vue/components/BaseButton.vue";
import PrivacyNoticeDialog from "~vue/Onboarding/PrivacyNoticeDialog.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import NadiaCard from "./NadiaCard.vue";
import StepActions from "./StepActions.vue";
import StepPoints from "./StepPoints.vue";
import StepShell from "./StepShell.vue";

const {
    firstName,
    managesPeople,
    embedded = false,
} = defineProps<{
    firstName: string;
    managesPeople: boolean;
    embedded?: boolean;
}>();
defineEmits<{ complete: [] }>();

const { t } = useI18n();

const privacyOpen = ref(false);

const title = computed(() => (managesPeople ? t("onboarding.calendarFirst.introStep.title.manager") : t("onboarding.calendarFirst.introStep.title.ic")));
const body = computed(() => (managesPeople ? t("onboarding.calendarFirst.introStep.body.manager") : t("onboarding.calendarFirst.introStep.body.ic")));

const points = computed(() => [
    {
        icon: "bi-people-fill",
        lead: t("onboarding.calendarFirst.introStep.points.relationships.lead"),
        text: managesPeople ? t("onboarding.calendarFirst.introStep.points.relationships.manager") : t("onboarding.calendarFirst.introStep.points.relationships.ic"),
    },
    {
        icon: "bi-flag-fill",
        lead: t("onboarding.calendarFirst.introStep.points.goals.lead"),
        text: managesPeople ? t("onboarding.calendarFirst.introStep.points.goals.manager") : t("onboarding.calendarFirst.introStep.points.goals.ic"),
    },
    {
        icon: "bi-megaphone-fill",
        lead: t("onboarding.calendarFirst.introStep.points.career.lead"),
        text: t("onboarding.calendarFirst.introStep.points.career.text"),
    },
]);

/* Keep these promises brief; PrivacyNoticeDialog contains the full privacy guarantees. */
const privacyPromises = computed(() => [
    t("onboarding.calendarFirst.introStep.privacy.conversations"),
    t("onboarding.calendarFirst.introStep.privacy.publish"),
    t("onboarding.calendarFirst.introStep.privacy.profile"),
]);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .greeting {
        @apply display-serif text-center text-ink;
    }

    .sections {
        @apply flex flex-col gap-5;
    }

    .card-body {
        @apply body-small text-ink-muted;
    }

    .privacy {
        @apply flex flex-col items-start gap-2.5 rounded-lgr bg-surface-sunken p-5;
    }

    .privacy-title {
        @apply body-bold text-ink;
    }

    .privacy-list {
        @apply body-small mt-1 list-disc pl-5 text-ink-muted;
    }

    .privacy-link {
        @apply -ml-2 inline-flex items-center gap-2.5;
    }
}

/*
 * Keep this rule unlayered to override the base-btn-sm utility and make the privacy link less prominent
 * than Next.
 */
.privacy-link {
    @apply body-small font-normal text-ink-muted;
}
</style>
